import {
  array,
  mixed,
  number,
  object,
  SchemaOf,
  string,
  StringSchema,
} from 'yup';

import {
  POD_SECURITY_MODE_FIELDS,
  POD_SECURITY_PROFILES,
  PRIVILEGED_REQUIRED_NAMESPACES,
  PodSecurityNamespaceEntry,
  PodSecurityProfile,
  PodSecurityStandardsFormValues,
} from './types';

const profileField = mixed<PodSecurityProfile>().oneOf(
  [...POD_SECURITY_PROFILES],
  'Invalid profile'
);

const namespaceEntrySchema = object({
  namespace: string().required('Namespace name is required').trim(),
  enforce: profileField,
  audit: profileField,
  warn: profileField,
})
  .test(
    'at-least-one-mode',
    'Set a profile for at least one of enforce, audit or warn',
    function validate(entry) {
      if (entry?.enforce || entry?.audit || entry?.warn) {
        return true;
      }
      return this.createError({ path: `${this.path}.enforce` });
    }
  )
  .test(
    'privileged-required',
    'System and Portainer-managed namespaces must use the privileged profile',
    function validate(entry) {
      const namespace = entry?.namespace?.trim() ?? '';
      const isPrivilegedRequired = (
        PRIVILEGED_REQUIRED_NAMESPACES as ReadonlyArray<string>
      ).includes(namespace);

      const hasNonPrivileged = POD_SECURITY_MODE_FIELDS.some(({ mode }) => {
        const profile = entry?.[mode];
        return profile !== undefined && profile !== 'privileged';
      });

      if (isPrivilegedRequired && hasNonPrivileged) {
        return this.createError({
          path: `${this.path}.namespace`,
          message: `"${namespace}" must use the privileged profile`,
        });
      }
      return true;
    }
  ) as SchemaOf<PodSecurityNamespaceEntry>;

export function createPodSecurityStandardsValidationSchema(
  nameValidation: StringSchema<string>
): SchemaOf<PodSecurityStandardsFormValues> {
  return object({
    name: nameValidation,
    environmentGroupIds: array(number().required()),
    namespaces: array(namespaceEntrySchema)
      .min(1, 'At least one namespace is required')
      .required('At least one namespace is required')
      .test(
        'unique-namespaces',
        'Namespaces must be unique',
        function validate(entries) {
          const list = entries ?? [];
          const seen = new Set<string>();
          for (let index = 0; index < list.length; index += 1) {
            const namespace = list[index]?.namespace?.trim() ?? '';
            if (namespace) {
              if (seen.has(namespace)) {
                return this.createError({
                  path: `${this.path}[${index}].namespace`,
                  message: `"${namespace}" is already configured`,
                });
              }
              seen.add(namespace);
            }
          }
          return true;
        }
      ),
  });
}
